/**
 * src/shell/modules/services/SovereignModuleStorage.ts
 *
 * Microkernel Scoped Key-Value Storage Backend (Sprint 7 / Issue 7.3).
 * Enforces per-module key namespacing, persistent local caching, and hard quota bounds.
 */

import type { StorageCapability } from '../../../../packages/module-sdk/src/types.js';

export const DEFAULT_CLIENT_QUOTA_BYTES = 5 * 1024 * 1024; // 5 MB

export class SovereignModuleStorage {
  private static instance: SovereignModuleStorage;
  private memoryFallback: Map<string, string> = new Map();

  private constructor() {}

  public static getInstance(): SovereignModuleStorage {
    if (!SovereignModuleStorage.instance) {
      SovereignModuleStorage.instance = new SovereignModuleStorage();
    }
    return SovereignModuleStorage.instance;
  }

  private getFullKey(moduleId: string, key: string): string {
    return `mod_kv:${moduleId}:${key}`;
  }

  private getModulePrefix(moduleId: string): string {
    return `mod_kv:${moduleId}:`;
  }

  /**
   * Calculates total stored byte size for a specific module
   */
  public getModuleUsageBytes(moduleId: string): number {
    const prefix = this.getModulePrefix(moduleId);
    let totalBytes = 0;

    if (typeof window !== 'undefined' && window.localStorage) {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          const v = localStorage.getItem(k) || '';
          totalBytes += k.length * 2 + v.length * 2; // UTF-16 approx bytes
        }
      }
    } else {
      for (const [k, v] of this.memoryFallback.entries()) {
        if (k.startsWith(prefix)) {
          totalBytes += k.length * 2 + v.length * 2;
        }
      }
    }

    return totalBytes;
  }

  public async get<T = unknown>(moduleId: string, key: string): Promise<T | undefined> {
    const fullKey = this.getFullKey(moduleId, key);
    let raw: string | null = null;

    if (typeof window !== 'undefined' && window.localStorage) {
      raw = localStorage.getItem(fullKey);
    } else {
      raw = this.memoryFallback.get(fullKey) || null;
    }

    if (raw === null || raw === undefined) return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  }

  public async set<T = unknown>(
    moduleId: string,
    key: string,
    value: T,
    quotaBytes = DEFAULT_CLIENT_QUOTA_BYTES
  ): Promise<void> {
    const fullKey = this.getFullKey(moduleId, key);
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    const itemBytes = fullKey.length * 2 + (serialized?.length || 0) * 2;

    const currentUsage = this.getModuleUsageBytes(moduleId);
    if (currentUsage + itemBytes > quotaBytes) {
      throw new Error(`QUOTA_EXCEEDED: Module "${moduleId}" exceeded client storage quota (${quotaBytes} bytes)`);
    }

    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(fullKey, serialized);
    } else {
      this.memoryFallback.set(fullKey, serialized);
    }
  }

  public async delete(moduleId: string, key: string): Promise<void> {
    const fullKey = this.getFullKey(moduleId, key);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(fullKey);
    } else {
      this.memoryFallback.delete(fullKey);
    }
  }

  public async listKeys(moduleId: string, subPrefix = ''): Promise<string[]> {
    const prefix = this.getModulePrefix(moduleId);
    const filterPrefix = prefix + subPrefix;
    const keys: string[] = [];

    if (typeof window !== 'undefined' && window.localStorage) {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(filterPrefix)) {
          keys.push(k.substring(prefix.length));
        }
      }
    } else {
      for (const k of this.memoryFallback.keys()) {
        if (k.startsWith(filterPrefix)) {
          keys.push(k.substring(prefix.length));
        }
      }
    }

    return keys;
  }

  /**
   * Factory that binds storage operations to a specific module context
   */
  public createScopedAdapter(moduleId: string, quotaBytes = DEFAULT_CLIENT_QUOTA_BYTES): StorageCapability {
    return {
      get: <T = unknown>(key: string) => this.get<T>(moduleId, key),
      set: <T = unknown>(key: string, value: T) => this.set<T>(moduleId, key, value, quotaBytes),
      delete: (key: string) => this.delete(moduleId, key),
      listKeys: (prefix?: string) => this.listKeys(moduleId, prefix),
      getItem: (key: string) => {
        const fullKey = this.getFullKey(moduleId, key);
        return typeof window !== 'undefined' && window.localStorage
          ? localStorage.getItem(fullKey)
          : this.memoryFallback.get(fullKey) || null;
      },
      setItem: (key: string, value: string) => {
        this.set(moduleId, key, value, quotaBytes).catch((err) => {
          console.warn(`[SovereignModuleStorage] setItem error in ${moduleId}:`, err.message);
        });
      },
      removeItem: (key: string) => {
        this.delete(moduleId, key).catch(() => {});
      },
    };
  }

  public clearAll(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('mod_kv:')) keysToDelete.push(k);
      }
      keysToDelete.forEach((k) => localStorage.removeItem(k));
    }
    this.memoryFallback.clear();
  }
}

export const sovereignModuleStorage = SovereignModuleStorage.getInstance();
